import { Circle, Group, Shape, Text } from "react-konva";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { ISeat } from "../../interface/ISeat";
import { ICategoryAssignments } from "../../interface/ICategoryAssignments";
import { IDataHover } from "../../interface/DataHover";
import { setSelectSeatOrderStore } from "../../features/selectSeatOrderSlice";
import { IBasket } from "../../interface/IBasket";
import { setAlertMsgStore } from "../../features/alertMsgSlisce";
import { setOrderInStore } from "../../features/orderSlice";
import { setOrderItemsStore } from "../../features/orderItemsSlice";
import { IOrder } from "../../interface/IOrder";
import { IOrderDetails } from "../../interface/IOrderDetails";
import { setSelectOrderDetailsInStore } from "../../features/selectOrderDetailsSlice";
import { ILocation } from "../../interface/ILocation";
import { ITenant } from "../../interface/ITenant";
import { setClientStore } from "../../features/clientSlice";
import { setSelectOrderDataInStore } from "../../features/selectOrderDataSlice";

// Типы
interface SeatProps {
  seat: ISeat;
  x: number;
  y: number;
  data: ISeat;
  isSelected: boolean;
  sessionAssignments: ICategoryAssignments[];
  showPaid: boolean;
  onHover: (data: IDataHover | null) => void;
  onSelect: (data: { id: string; groupTag: string | null; assignmentType: number }) => void;
  onDeselect: (data: { id: string; groupTag: string | null; assignmentType: number }) => void;
  editorMode: boolean
}

interface SeatShape {
  id: number;
  width: number;
  height: number;
  type: string;
  stroke: string;
  strokeWidth: number;
  ctxs: Array<{
    type: string;
    x: number;
    y: number;
    cpx?: number;
    cpy?: number;
  }>;
}

interface RenderShapeProps {
  props: SeatProps;
  object: SeatShape;
  isBooked: boolean;
}

// Константы
const SEAT_SIZE = 30;
const SEAT_CENTER = SEAT_SIZE / 2;
const ASSIGNMENT_TYPE = 3;
// const SHOW_RESERVED_SEATS = true;

// Утилиты - исправлено: убрал useSelector из обычной функции
const getSeatColor = (
  isBooked: boolean,
  isSelected: boolean,
  fillColor: string,
  showSeatStatus: boolean,
  statusOrder?: number,
  clientId?: string | null
): string => {
  if (showSeatStatus) {
    if (statusOrder === 5) {
      return "#93ffd2ff"
    }
    if (statusOrder === 2) {
      if (clientId) {
        return "#bfc200ff"
      } else {
        return "#c20000ff"
      }
    }
  }
  if (isSelected) return "black";
  if (isBooked) return "#adadadff";
  return fillColor;
};

const createSeatShape = (id: number, ctxs: SeatShape['ctxs']): SeatShape => ({
  id,
  width: SEAT_SIZE,
  height: SEAT_SIZE,
  type: "shape",
  stroke: '#808080',
  strokeWidth: 1,
  ctxs,
});

// Предопределенные формы сидений
const SEAT_SHAPES: Record<number, SeatShape> = {
  1: { ...createSeatShape(1, []), type: "circle" },
  2: createSeatShape(2, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 20 }, { type: "curve", cpx: 30, cpy: 30, x: 20, y: 30 },
    { type: "line", x: 25, y: 30 }, { type: "line", x: 10, y: 30 },
    { type: "curve", cpx: 0, cpy: 30, x: 0, y: 20 }, { type: "line", x: 0, y: 25 },
    { type: "line", x: 0, y: 0 },
  ]),
  3: createSeatShape(3, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 10, y: 30 },
    { type: "curve", cpx: 0, cpy: 30, x: 0, y: 20 }, { type: "line", x: 0, y: 20 },
    { type: "line", x: 0, y: 0 },
  ]),
  4: createSeatShape(4, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 20 }, { type: "curve", cpx: 30, cpy: 30, x: 20, y: 30 },
    { type: "line", x: 25, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 0 },
  ]),
  5: createSeatShape(5, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 0 },
  ]),
  6: createSeatShape(6, [
    { type: "line", x: 5, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 5 }, { type: "curve", cpx: 0, cpy: 0, x: 5, y: 0 },
  ]),
  7: createSeatShape(7, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 25, y: 0 },
    { type: "line", x: 25, y: 0 }, { type: "curve", cpx: 30, cpy: 0, x: 30, y: 5 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 0 },
  ]),
  8: createSeatShape(8, [
    { type: "line", x: 5, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 5 }, { type: "curve", cpx: 0, cpy: 0, x: 5, y: 0 },
  ]),
  9: createSeatShape(9, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 25, y: 0 },
    { type: "line", x: 25, y: 0 }, { type: "curve", cpx: 30, cpy: 0, x: 30, y: 5 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 0 },
  ]),
  10: createSeatShape(10, [
    { type: "line", x: 5, y: 0 }, { type: "line", x: 25, y: 0 },
    { type: "curve", cpx: 30, cpy: 0, x: 30, y: 5 }, { type: "line", x: 30, y: 30 },
    { type: "line", x: 0, y: 30 }, { type: "line", x: 0, y: 5 },
    { type: "curve", cpx: 0, cpy: 0, x: 5, y: 0 },
  ]),
  11: createSeatShape(11, [
    { type: "line", x: 5, y: 0 }, { type: "line", x: 30, y: 0 },
    { type: "line", x: 30, y: 30 }, { type: "line", x: 30, y: 30 },
    { type: "line", x: 5, y: 30 }, { type: "curve", cpx: 0, cpy: 30, x: 0, y: 25 },
    { type: "line", x: 0, y: 5 }, { type: "curve", cpx: 0, cpy: 0, x: 5, y: 0 },
  ]),
  12: createSeatShape(12, [
    { type: "line", x: 0, y: 0 }, { type: "line", x: 25, y: 0 },
    { type: "curve", cpx: 30, cpy: 0, x: 30, y: 5 }, { type: "line", x: 30, y: 25 },
    { type: "curve", cpx: 30, cpy: 30, x: 25, y: 30 }, { type: "line", x: 0, y: 30 },
    { type: "line", x: 0, y: 0 },
  ]),
};

const RenderShape = ({ props, object, isBooked }: RenderShapeProps) => {
  // Получаем showSeatStatus здесь, внутри компонента
  const showSeatStatus = useSelector((state: any) => state.showSeatStatus, shallowEqual) as boolean;

  const assignment = props.sessionAssignments?.find(
    (cat: ICategoryAssignments) => cat.targetId === props.seat.id
  );

  const orderStatus = assignment?.paidOrders?.[0]?.orderStatus;
  const clientId = assignment?.latestOrder?.clientId;

  const fillColor = getSeatColor(
    isBooked,
    props.isSelected,
    assignment?.color ?? "#a3a3a3",
    showSeatStatus,
    orderStatus ?? null,
    clientId ?? null
  );

  if (object.type === 'circle') {
    return (
      <Circle
        x={props.x + SEAT_CENTER}
        y={props.y + SEAT_CENTER}
        fill={fillColor}
        width={object.width}
        height={object.height}
      />
    );
  }

  return (
    <Shape
      x={props.x + SEAT_CENTER}
      y={props.y + SEAT_CENTER}
      offsetX={SEAT_CENTER}
      offsetY={SEAT_CENTER}
      fill={fillColor}
      width={object.width}
      height={object.height}
      rotation={props.data.angle}
      sceneFunc={(context, shape) => {
        context.beginPath();
        object.ctxs.forEach((ctx) => {
          switch (ctx.type) {
            case 'move':
              context.moveTo(ctx.x, ctx.y);
              break;
            case 'line':
              context.lineTo(ctx.x, ctx.y);
              break;
            case 'curve':
              context.quadraticCurveTo(ctx.cpx!, ctx.cpy!, ctx.x, ctx.y);
              break;
            default:
              break;
          }
        });
        context.closePath();
        context.fillStrokeShape(shape);
      }}
    />
  );
};

const GetFigureSeat = ({ props, isBooked }: { props: SeatProps; isBooked: boolean }) => {
  const seatShape = useMemo(() =>
    SEAT_SHAPES[props.data.seatType] || SEAT_SHAPES[2],
    [props.data.seatType]
  );

  return <RenderShape props={props} object={seatShape} isBooked={isBooked} />;
};

const useSeatHover = () => {
  const calculateTextPosition = useCallback((target: any, pos: { x: number; y: number }) => {
    if (target.className !== 'Text') return pos;

    const textWidth = target.getWidth();
    const textHeight = target.getHeight();
    const align = target.attrs.align || 'center';
    const verticalAlign = target.attrs.verticalAlign || 'middle';

    let offsetX = textWidth / 2 - 3.5;
    let offsetY = textHeight / 2 - 3.5;

    if (align === 'left') offsetX = 0;
    else if (align === 'right') offsetX = textWidth;
    if (verticalAlign === 'top') offsetY = 0;
    else if (verticalAlign === 'bottom') offsetY = textHeight;

    return {
      x: pos.x + offsetX,
      y: pos.y + offsetY
    };
  }, []);

  const handleMouseEnter = useCallback((e: any, props: SeatProps, isBooked: boolean) => {
    const pos = e.target.getAbsolutePosition();
    e.target._clearCache();
    const container = e.target.getStage().container();

    const finalPos = calculateTextPosition(e.target, pos);

    if (props.showPaid) {
      const assignment = props.sessionAssignments.find(
        (cat: ICategoryAssignments) => cat.targetId === props.seat.id
      );

      if (assignment?.paidOrders?.length) {
        container.style.cursor = "help";
        const dataHover: IDataHover = {
          id: props.seat.id,
          row: props.seat.row,
          place: props.seat.place,
          price: 0,
          cursor: 'pointer',
          pos: finalPos,
          assignmentType: ASSIGNMENT_TYPE,
          orderId: assignment.paidOrders[0].orderId
        };
        props.onHover(dataHover);
        return;
      }
    }

    container.style.cursor = isBooked ? "not-allowed" : "pointer";

    if (!isBooked) {
      const dataHover: IDataHover = {
        id: props.seat.id,
        row: props.seat.row,
        place: props.seat.place,
        price: 0,
        cursor: 'pointer',
        pos: finalPos,
        assignmentType: ASSIGNMENT_TYPE
      };
      props.onHover(dataHover);
    }
  }, [calculateTextPosition]);

  return handleMouseEnter;
};

const CircleSelect = ({ props }: { props: SeatProps }) => {
  const assignment = props.sessionAssignments.find(
    (cat: ICategoryAssignments) => cat.targetId === props.seat.id
  );

  return (
    <Circle
      width={40}
      height={40}
      fill={assignment?.color ?? '#000000'}
      shadowColor="black"
      x={props.x + SEAT_CENTER}
      y={props.y + SEAT_CENTER}
    />
  );
};

export const Seat = (props: SeatProps) => {
  const [groupTag, setGroupTag] = useState<string | null>(null);
  const [isBooked, setIsBooked] = useState<boolean>(true);
  const [distributorAssignment, setDistributorAssignment] = useState<boolean>(false);
  const seatGroupRef = useRef<any>(null);
  const handleMouseEnter = useSeatHover();

  // Redux selectors
  const distributorAssignments = useSelector((state: any) => state.distributorAssignments, shallowEqual);
  const locationScheme = useSelector((state: any) => state.locationScheme, shallowEqual) as ILocation;
  const basket = useSelector((state: any) => state.basket, shallowEqual) as IBasket[];
  const order = useSelector((state: any) => state.order, shallowEqual) as IOrder;
  const selectOrderDetails = useSelector((state: any) => state.selectOrderDetails, shallowEqual) as IOrderDetails;
  const sessionAssignments = useSelector((state: any) => state.sessionAssignments, shallowEqual) as ICategoryAssignments[];
  const draggableSeat = useSelector((state: any) => state.draggableSeat, shallowEqual) as boolean;
  const tenant = useSelector((state: any) => state.tenant) as ITenant;

  const dispatch = useDispatch();

  // Получение информации о назначении места
  const seatAssignment = useMemo(() =>
    sessionAssignments?.find((cat: ICategoryAssignments) => cat.targetId === props.seat.id),
    [sessionAssignments, props.seat.id]
  );

  // Обновление статуса бронирования
  useEffect(() => {
    if (seatAssignment) {
      setIsBooked(seatAssignment.orderItemsQuantity >= seatAssignment.limit);
      setGroupTag(seatAssignment.groupTag || null);
    } else {
      setIsBooked(false);
      setGroupTag(null);
    }
  }, [seatAssignment]);

  // Обновление distributor assignment
  useEffect(() => {
    if (selectOrderDetails?.seats.length) {
      const hasAssignment = selectOrderDetails.seats.some(
        (item: any) => item.priceAndQuantity.targetId === props.seat.id
      );
      setDistributorAssignment(hasAssignment);
    } else {
      setDistributorAssignment(false);
    }
  }, [selectOrderDetails, props.seat.id]);

  useEffect(() => {
    if (distributorAssignments && seatAssignment) {
      const hasDistributor = distributorAssignments.some(
        (dis: any) => dis.priceCategoryAssignmentId === seatAssignment.id
      );
      setDistributorAssignment(hasDistributor);
    } else {
      setDistributorAssignment(false);
    }
  }, [distributorAssignments, seatAssignment]);

  const handleClickOnSeat = useCallback(() => {
    if (props.showPaid && seatAssignment?.paidOrders?.length) {
      if (!basket?.length) {
        dispatch(setOrderInStore(null as any))
        dispatch(setSelectSeatOrderStore(seatAssignment.paidOrders[0].orderId));
        return;
      }

      if (basket.length > 0) {
        dispatch(setAlertMsgStore({
          title: "Невозможно добавить места из другого заказа в текущий",
          variant: 'warning'
        } as any));
        return;
      }

      if (order) {
        dispatch(setOrderInStore(null as any));
        dispatch(setOrderItemsStore(null));
        localStorage.removeItem("orderd");
        dispatch(setSelectSeatOrderStore(seatAssignment.paidOrders[0].orderId));
        return;
      }
    }

    dispatch(setSelectSeatOrderStore(null as any));
    dispatch(setSelectOrderDetailsInStore(null as any));
    dispatch(setClientStore(null as any));
    dispatch(setSelectOrderDataInStore(null as any));

    if (isBooked) return;

    const seatData = { id: props.data.id, groupTag: null, assignmentType: ASSIGNMENT_TYPE };

    if (distributorAssignment) {
      props.onDeselect(seatData);
    } else if (props.isSelected) {
      props.onDeselect(seatData);
    } else {
      props.onSelect(seatData);
    }
  }, [
    props, isBooked, distributorAssignment, seatAssignment, basket, order, dispatch
  ]);

  useCallback(() => {
    if (props.showPaid && seatAssignment?.paidOrders?.length) {
      if (!basket?.length) {
        dispatch(setOrderInStore(null as any))
        dispatch(setSelectSeatOrderStore(seatAssignment.paidOrders[0].orderId));
        return;
      }

      if (basket.length > 0) {
        dispatch(setAlertMsgStore({
          title: "Невозможно добавить места из другого заказа в текущий",
          variant: 'warning'
        } as any));
        return;
      }

      if (order) {
        dispatch(setOrderInStore(null as any));
        dispatch(setOrderItemsStore(null));
        localStorage.removeItem("orderd");
        dispatch(setSelectSeatOrderStore(seatAssignment.paidOrders[0].orderId));
        return;
      }
    }

    dispatch(setSelectSeatOrderStore(null as any));
    dispatch(setSelectOrderDetailsInStore(null as any));
    dispatch(setClientStore(null as any));
    dispatch(setSelectOrderDataInStore(null as any));

    if (isBooked) return;

    const seatData = { id: props.data.id, groupTag: null, assignmentType: ASSIGNMENT_TYPE };

    if (distributorAssignment) {
      props.onDeselect(seatData);
    } else if (props.isSelected) {
      props.onDeselect(seatData);
    } else {
      props.onSelect(seatData);
    }
  }, [
    props, isBooked, distributorAssignment, seatAssignment, basket, order, dispatch
  ]
  );


  const handleMouseLeave = useCallback((e: any) => {
    props.onHover(null);
    const container = e.target.getStage().container();
    container.style.cursor = "";
  }, [props]);

  return (
    <Group
      ref={seatGroupRef}
      onClick={handleClickOnSeat}
      onTap={handleClickOnSeat}
      onMouseEnter={(e) => handleMouseEnter(e, props, isBooked)}
      onMouseLeave={handleMouseLeave}
      draggable={draggableSeat}
    >
      {distributorAssignment && <CircleSelect props={props} />}
      <GetFigureSeat props={props} isBooked={isBooked} />

      {!groupTag && !props.isSelected && !isBooked && (
        <Text
          text={props.data.place}
          x={props.x}
          y={props.y}
          fill="#fff"
          fontSize={SEAT_SIZE / 1.9}
          align="center"
          verticalAlign="middle"
          width={SEAT_SIZE}
          height={SEAT_SIZE}
        />
      )}

      {props.isSelected && (
        <Circle
          width={15}
          height={15}
          fill="#fff"
          x={props.x + SEAT_CENTER}
          y={props.y + SEAT_CENTER}
        />
      )}
    </Group>
  );
};